import { takeLatest, put, call, fork, select, debounce, take } from 'redux-saga/effects';

import { restClient } from 'config/store/configureStore';

import { SELECT_DEALER } from 'constants/ActionTypes';
import * as allTypes from 'constants/ActionTypes';
import { APPOINTMENT_TASKS_STATUS } from 'constants/AppointmentConstants';
import { CODE_ERRORS, DEBOUNCE_TIME } from 'constants/Global';
import { PATH } from 'constants/RoutesConstants';

import { fetchEvents } from 'modules/appointments/actions/details';
import { fetchLockerAppointmentTasks } from 'modules/appointmentTask/actions';
import { POST_PREPARE_SUCCESS, POST_VERIFY_SUCCESS, POST_UNPREPARE_SUCCESS } from 'modules/appointmentTask/actionTypes';

import { selectDealer } from 'modules/dealers/actions';
import { createNotification } from 'modules/toast-notifications/actions';

import { getTimeZoneOffsetByDealerId } from 'selectors/dealerSelectors';

import { convertTimeZones } from 'utils/date';

import * as actions from './actions';
import * as actionTypes from './actionTypes';
import { getSelectedLockerId, getUpdatingVaultId } from './selectors';
import { getParams } from './selectors/dashboard';
import { getCurrentLocation } from '../../selectors/router';
import { applicationIsReady } from '../app/selectors';
import { getId } from './selectors/ui/details/nextAppointmentTask';

function* refreshLockerPageWorker(action) {
  const lockerId = yield select(getSelectedLockerId);
  const { appointmentTaskId } = action.meta;
  const nextAppointmentTaskId = yield select(getId);

  const canRefresh = appointmentTaskId && appointmentTaskId === nextAppointmentTaskId;
  if (canRefresh) {
    yield put(actions.fetchVault({ id: lockerId }));
  }
}

export function* fetchVaultWorker(action) {
  const { request } = action.payload;
  try {
    const response = yield call(restClient.request, request);
    yield put(actions.fetchVaultSuccess(response.data));

    const dealerId = response.data?.dealer?.id ?? response.data?.dealerId;
    yield put(selectDealer(dealerId));
    const { error } = yield take([allTypes.FETCH_DEALER_SUCCESS, allTypes.FETCH_DEALER_FAILURE]);

    if (!error) {
      const timeZoneOffset = yield select(state => getTimeZoneOffsetByDealerId(state, dealerId));

      yield put(
        fetchLockerAppointmentTasks({
          vaultId: request?.params?.id,
          from: convertTimeZones(new Date(), timeZoneOffset, Intl.DateTimeFormat().resolvedOptions().timeZone).toISOString(),
          status: APPOINTMENT_TASKS_STATUS.TO_START,
          nextAppointmentTaskId: response.data?.nextAppointmentTask?.id,
          size: 5,
        })
      );
    }
  } catch (error) {
    yield put(actions.fetchVaultFailure(error));
  }
}

function* putDeactivateLockerWorker(action) {
  const { request } = action.payload;
  try {
    const response = yield call(restClient.request, request);
    yield put(actions.deactivateLockerSuccess(response.data));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.fiche.notification.locker_deactivated.success',
          defaultMessage: 'Safe disabled',
        },
      })
    );
  } catch (error) {
    yield put(actions.deactivateLockerFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.fiche.notification.locker_deactivated.failure',
          defaultMessage: 'Can’t disable the safe',
        },
      })
    );
  }
}

function* putReactivateLockerWorker(action) {
  const { request } = action.payload;
  try {
    const response = yield call(restClient.request, request);
    yield put(actions.reactivateLockerSuccess(response.data));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.fiche.notification.locker_reactivated.success',
          defaultMessage: 'Safe enabled',
        },
      })
    );
  } catch (error) {
    yield put(actions.reactivateLockerFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.fiche.notification.locker_reactivated.failure',
          defaultMessage: 'Can’t enable the safe',
        },
      })
    );
  }
}

function* fetchAppointmentTasksEventsWorker(action) {
  const { payload, meta } = action;
  const { request } = payload;
  try {
    const response = yield call(restClient.request, request);
    yield put(actions.fetchAppointmentTasksEventsSuccess(response.data, meta));
  } catch (error) {
    yield put(actions.fetchAppointmentTasksEventsFailure(error));
  }
}

function* generateRandomCodeWorker({ payload, meta }) {
  const { request } = payload;
  try {
    const { data: code } = yield call(restClient.request, request);
    yield put(actions.generateRadomCodeSuccess(code, meta));
  } catch (error) {
    yield put(actions.generateRandomCodeFailure(error));
  }
}

function* validateCodeWorker({ payload, meta }) {
  const { request } = payload;
  try {
    yield call(restClient.request, request);
    yield put(actions.validateLockerCodeSuccess(true, meta));
  } catch (error) {
    yield put(actions.validateLockerCodeFailure({ message: error }, meta));
  }
}

function* patchCodeWorker({ payload, meta }) {
  const { request } = payload;
  try {
    const { data: vault } = yield call(restClient.request, request);
    yield put(actions.patchLockerCodeSuccess(vault, meta));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.fiche.notification.locker_code_update.success',
          defaultMessage: 'Safe code updated',
        },
      })
    );
  } catch (error) {
    yield put(actions.patchLockerCodeFailure(error, meta));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.fiche.notification.locker_code_update.failure',
          defaultMessage: 'Can’t update the safe code',
        },
      })
    );
  }
}

function* createSafeWorker({ payload, meta }) {
  const { request } = payload;
  try {
    const { data: vault } = yield call(restClient.request, request);
    yield put(actions.createLockerSuccess(vault, meta));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.config.dealer_general.safes.notification.create.success',
          defaultMessage: 'Safe created',
        },
      })
    );
  } catch (error) {
    yield put(actions.createLockerFailure(error, meta));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'http.error.message;',
          defaultMessage: 'Oops! Something went wrong.',
        },
      })
    );
  }
}

function* updateSafeWorker({ payload, meta }) {
  const { request } = payload;
  try {
    const { data: vault } = yield call(restClient.request, request);
    yield put(actions.createLockerSuccess(vault, meta));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.config.dealer_general.safes.notification.update.success',
          defaultMessage: 'Safe updated',
        },
      })
    );
  } catch (error) {
    yield put(actions.createLockerFailure(error, meta));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.config.dealer_general.safes.notification.update.failure',
          defaultMessage: "Can't update the safe",
        },
      })
    );
  }
}

function* deleteSafeWorker({ payload, meta }) {
  const { request, id } = payload;
  try {
    yield call(restClient.request, request);
    yield put(actions.deleteLockerSuccess({ id }, meta));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.config.dealer_general.safes.notification.delete.success',
          defaultMessage: 'Safe deleted',
        },
      })
    );
  } catch (error) {
    yield put(actions.deleteLockerFailure(error, meta));
    const isConflict = error.response?.status === CODE_ERRORS.CONFLICT_ERROR;
    let message = {
      id: 'page.config.dealer_general.safes.notification.delete.failure',
      defaultMessage: "Can't delete the safe",
    };
    if (isConflict) {
      message = {
        id: 'page.config.dealer_general.safes.notification.delete.conflict',
        defaultMessage: "Can't delete the safe with upcoming appointments ",
      };
    }
    yield put(
      createNotification({
        type: 'error',
        message,
      })
    );
  }
}

function* validateAfterSetCodeWorker({ payload: code }) {
  const vaultId = yield select(getUpdatingVaultId);
  yield put(actions.validateLockerCode(code, vaultId));
}

function* getLockersWorker({ payload, meta }) {
  const { request } = payload;
  try {
    const { data } = yield call(restClient.request, request);
    yield put(actions.getLockersSuccess(data, meta));
  } catch (error) {
    yield put(actions.getLockersFailure(error, meta));
  }
}

function* refreshDashboardWorker({ payload: additionalParams = {} }) {
  const isReady = yield select(applicationIsReady);

  if (isReady) {
    const params = yield select(getParams);
    yield put(actions.getLockers({ ...params, ...additionalParams }));
  }
}

/**
 * @param {import('modules/dealers/types').SelectDealerAction} action
 */
function* afterSetDealerWorker({ payload }) {
  const location = yield select(getCurrentLocation);
  const isReady = yield select(applicationIsReady);

  if (isReady && location.includes(PATH.SAFE_CONFIG.path)) {
    yield put(actions.refreshLockerDashboard({ dealerId: payload.dealerId }));
  }
}

function* openShareBoxLocker({ payload, meta }) {
  const { request } = payload;
  const { appointmentId } = meta;

  try {
    yield call(restClient.request, request);
    yield put(actions.openShareboxLockerSuccess());
    yield put(fetchEvents(appointmentId));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.keyDeposit.appointments.modal.openLocker.success',
          defaultMessage: 'The locker has been opened with success',
        },
      })
    );
  } catch (error) {
    yield put(actions.openShareboxLockerFailure(error));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.keyDeposit.appointments.modal.openLocker.failure',
          defaultMessage: 'An error has occurred while opening the locker',
        },
      })
    );
  }
}

function* fetchVaultWatcher() {
  yield takeLatest(actionTypes.FETCH_VAULT_REQUEST, fetchVaultWorker);
}

function* fetchAppointmentTasksEventsWatcher() {
  yield takeLatest(actionTypes.FETCH_APPOINTMENT_TASKS_EVENTS_REQUEST, fetchAppointmentTasksEventsWorker);
}

function* putReactivateLockerWatcher() {
  yield takeLatest(actionTypes.REACTIVATE_LOCKER_REQUEST, putReactivateLockerWorker);
}

function* putDeactivateLockerWatcher() {
  yield takeLatest(actionTypes.DEACTIVATE_LOCKER_REQUEST, putDeactivateLockerWorker);
}

function* refreshLockerPageWatcher() {
  yield takeLatest([POST_PREPARE_SUCCESS, POST_UNPREPARE_SUCCESS, POST_VERIFY_SUCCESS], refreshLockerPageWorker);
}

function* generateRandomCodeWatcher() {
  yield takeLatest(actionTypes.FETCH_GENERATE_CODE_REQUEST, generateRandomCodeWorker);
}

function* patchCodeWatcher() {
  yield takeLatest(actionTypes.PATCH_CODE_REQUEST, patchCodeWorker);
}

function* validateAfterSetCodeWatcher() {
  yield takeLatest(actionTypes.SET_UPDATING_CODE, validateAfterSetCodeWorker);
}

function* validateCodeWatcher() {
  yield debounce(DEBOUNCE_TIME, actionTypes.FETCH_VALIDATE_CODE_REQUEST, validateCodeWorker);
}

function* createSafeWatcher() {
  yield takeLatest(actionTypes.POST_LOCKER_REQUEST, createSafeWorker);
}

function* updateSafeWatcher() {
  yield takeLatest(actionTypes.PUT_LOCKER_REQUEST, updateSafeWorker);
}

function* deleteSafeWatcher() {
  yield takeLatest(actionTypes.DELETE_LOCKER_REQUEST, deleteSafeWorker);
}

function* refreshDashboardWatcher() {
  yield takeLatest([actionTypes.REFRESH_DASHBOARD, actionTypes.POST_LOCKER_SUCCESS], refreshDashboardWorker);
}

function* getLockersWatcher() {
  yield takeLatest(actionTypes.GET_LOCKERS_REQUEST, getLockersWorker);
}

function* afterSetDealerWatcher() {
  yield takeLatest(SELECT_DEALER, afterSetDealerWorker);
}

function* afterSetFilterWatcher() {
  yield debounce(DEBOUNCE_TIME, actionTypes.SET_DASHBOARD_FILTERS, refreshDashboardWorker);
}

function* openShareboxLockerWatcher() {
  yield takeLatest(actionTypes.OPEN_SHAREBOX_LOCKER_REQUEST, openShareBoxLocker);
}

const LockersSagas = [
  fork(fetchVaultWatcher),
  fork(fetchAppointmentTasksEventsWatcher),
  fork(putDeactivateLockerWatcher),
  fork(putReactivateLockerWatcher),
  fork(refreshLockerPageWatcher),
  fork(generateRandomCodeWatcher),
  fork(validateCodeWatcher),
  fork(patchCodeWatcher),
  fork(validateAfterSetCodeWatcher),
  fork(createSafeWatcher),
  fork(updateSafeWatcher),
  fork(deleteSafeWatcher),
  fork(refreshDashboardWatcher),
  fork(getLockersWatcher),
  fork(afterSetDealerWatcher),
  fork(afterSetFilterWatcher),
  fork(openShareboxLockerWatcher),
];
export default LockersSagas;
